/* eslint-disable consistent-return */
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import { IcoCheck } from "assets/icons";
import { Button, Checkbox, Input } from "components";
import { FiscalDocumentHelper, StringHelper } from "helpers";
import { toast } from "react-toastify";
import { createOrUpdatePartnerAccount, getPartnerUuid } from "services/auth";
import { createNewPartner, getPartner } from "services/partner";
import { RootState, store } from "store";
import { setRegistrationDirty } from "store/features/flags/slice";
import { setPersonalInfo, setStep } from "store/features/registration/slice";
import { setFcm } from "store/features/tokens/slice";
import { requestFirebaseNotificationPermission } from "../../../firebase/firebase-messaging";
import styles from "./styles.module.scss";

const RegistrationSchema = Yup.object().shape({
	name: Yup.string()
		.required("Campo obrigatório")
		.max(100, "Limite máximo de 100 caracteres ultrapassado."),
	fiscal_id: Yup.string()
		.required("Campo obrigatório")
		.matches(
			/(\d{3}.?\d{3}.?\d{3}-?\d{2}|\d{2}.?\d{3}.?\d{3}-?\d{4}\/?\d{2})$/,
			"Campo inválido"
		)
		.test("is-fiscal-doc-valid", "Documento inválido", (value) => {
			if (value.replace(/\.|-|\//g, "").length === 11) {
				return FiscalDocumentHelper.validateCPF(value);
			}
			if (value.replace(/\.|-|\//g, "").length === 14) {
				return FiscalDocumentHelper.validateCNPJ(value);
			}
		}),
	email: Yup.string().required("Campo obrigatório"),
	cellphone: Yup.string()
		.required("Campo obrigatório")
		.matches(/^\(?\d{2}\)? ?\d{4,5}-?\d{4} *$/, "Campo inválido"),
	password: Yup.string()
		.required("Campo obrigatório")
		.min(8, "Campo inválido, verifique as instruções abaixo")
		.matches(/[A-Z]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[0-9]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[!@#$%^&*+_=]/, "Campo inválido, verifique as instruções abaixo"),
	passwordConfirmation: Yup.string()
		.required("Campo obrigatório")
		.oneOf(
			[Yup.ref("password")],
			"A confirmação de senha deve ser igual à senha"
		),
	accept: Yup.boolean().oneOf([true], "Você precisa aceitar os termos"),
	notifications: Yup.boolean()
});

const UpdateSchema = Yup.object().shape({
	name: Yup.string()
		.required("Campo obrigatório")
		.max(100, "Limite máximo de 100 caracteres ultrapassado."),
	fiscal_id: Yup.string()
		.required("Campo obrigatório")
		.matches(
			/(\d{3}.?\d{3}.?\d{3}-?\d{2}|\d{2}.?\d{3}.?\d{3}-?\d{4}\/?\d{2})$/,
			"Campo inválido"
		)
		.test("is-fiscal-doc-valid", "Documento inválido", (value) => {
			if (value.replace(/\.|-|\//g, "").length === 11) {
				return FiscalDocumentHelper.validateCPF(value);
			}
			if (value.replace(/\.|-|\//g, "").length === 14) {
				return FiscalDocumentHelper.validateCNPJ(value);
			}
		}),
	email: Yup.string().required("Campo obrigatório"),
	cellphone: Yup.string()
		.required("Campo obrigatório")
		.matches(/^\(?\d{2}\)? ?\d{4,5}-?\d{4} *$/, "Campo inválido"),
	notifications: Yup.boolean()
});

interface PasswordInstructionProps {
	isValid: boolean;
	label: string;
}
function PasswordInstruction({
	isValid = false,
	label
}: PasswordInstructionProps) {
	return (
		<div className="flex flex-row items-center">
			<IcoCheck color={isValid ? "#5E219C" : "#868F9C"} className="mr-1" />
			<span
				translate="no"
				className={`text-[12px] font-normal leading-none ${
					isValid ? "text-neutral-900" : "text-[#4B5563]"
				} font-poppins`}
			>
				{label}
			</span>
		</div>
	);
}

type PersonalInformationFormValues = {
	id: string;
	name: string;
	fiscal_id: string;
	email: string;
	cellphone: string;
	password: string;
	passwordConfirmation: string;
	accept: boolean;
	notifications: boolean;
	fcm_token: string | null;
};

export default function PersonalInformationPage() {
	const navigate = useNavigate();
	const registrationInfo = store.getState().registration;

	const [submitting, setSubmitting] = useState<boolean>(false);
	const [partnerId, setPartnerId] = useState("");

	const [fcmToken, setFcmToken] = useState<string>("");

	const [personalInformation, setPersonalInformation] =
		useState<PersonalInformationFormValues>({
			id: "",
			name: "",
			fiscal_id: "",
			email: "",
			cellphone: "",
			password: "",
			passwordConfirmation: "",
			accept: false,
			notifications: false,
			fcm_token: null
		});

	const dispatch = useDispatch();

	const { logged_user } = useSelector((state: RootState) => state.tokens);

	const fetchData = async () => {
		if (!logged_user) return;

		const partnerUuid = (await getPartnerUuid()) || "";
		setPartnerId(partnerUuid);

		const apiPersonalInformation: PersonalInformationFormValues =
			(await getPartner(partnerUuid)) || personalInformation;

		setPersonalInformation({
			...personalInformation,
			...apiPersonalInformation,
			email: logged_user.email,
			cellphone: logged_user.phone,
			notifications: Boolean(apiPersonalInformation.fcm_token)
		});

		setFcmToken(apiPersonalInformation.fcm_token || "");
	};

	useEffect(() => {
		dispatch(setStep(1));
		fetchData();
	}, []);

	const formik = useFormik({
		initialValues: personalInformation,
		validationSchema: logged_user ? UpdateSchema : RegistrationSchema,
		enableReinitialize: true,
		onSubmit: async ({
			name,
			fiscal_id,
			email,
			cellphone,
			password,
			passwordConfirmation,
			...rest
		}) => {
			setSubmitting(true);
			cellphone = StringHelper.sanitizeString(cellphone);
			dispatch(
				setPersonalInfo({
					...registrationInfo,
					name: StringHelper.sanitizeString(name),
					document: StringHelper.sanitizeString(fiscal_id),
					email: StringHelper.sanitizeString(email),
					cellphone: StringHelper.sanitizeString(cellphone),
					...rest
				})
			);

			await createOrUpdatePartnerAccount(
				logged_user,
				email,
				cellphone,
				password,
				passwordConfirmation
			)
				.then(async (account) => {
					await createNewPartner(partnerId, name, fiscal_id, fcmToken);

					if (fcmToken) {
						dispatch(setFcm(fcmToken));
					}

					return account;
				})
				.then(async (account) => {
					setSubmitting(false);
					dispatch(setRegistrationDirty(true));
					navigate(logged_user ? "/perfil" : "/cadastro/veiculos");
				})
				.catch((error) => {
					setSubmitting(false);
					let message = "Algo saiu errado. ";
					const errorTranslations: Record<string, string> = {
						"usuário with this email already exists.":
							"Já existe um usuário com este e-mail",
						"usuário with this phone already exists.":
							"Já existe um usuário com este telefone",
						"Parceiro with this fiscal id already exists.":
							"Já existe um parceiro com este CPF ou CNPJ",
						"Usuário com este phone já existe.":
							"Já existe um usuário com este telefone"
					};

					if (error.code < 500) {
						message += "Verifique os dados digitados e tente novamente.";
						if (error.body.email)
							formik.errors.email =
								errorTranslations[error.body.email] || error.body.email;
						if (error.body.phone)
							formik.errors.cellphone =
								errorTranslations[error.body.phone] || error.body.phone;
						if (error.body.fiscal_id)
							formik.errors.fiscal_id =
								errorTranslations[error.body.fiscal_id] || error.body.fiscal_id;
					} else {
						message += "Entre em contato com o suporte.";
					}

					toast.error(message, {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});
				});
		}
	});

	const onChangeNotifications = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.checked) {
			requestFirebaseNotificationPermission().then((token) => {
				if (token) {
					setFcmToken(token);
				} else {
					setFcmToken("");
				}
			});
		} else {
			setFcmToken("");
		}
		formik.handleChange(e);
	};

	const openUseTerms = () => {
		window.open("/XX_VANBORA contrato empresas_6-.pdf", "_blank", "noreferrer");
	};

	const openPrivacyPolicy = () => {
		window.open("/XX_VANBORA contrato empresas_6-.pdf", "_blank", "noreferrer");
	};

	return (
		<form onSubmit={formik.handleSubmit} className={styles.form}>
			<div className={styles.container}>
				<Input
					id="name"
					name="name"
					placeholder="Digite seu nome completo"
					label="Nome"
					value={formik.values.name || ""}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.name && formik.errors.name}
					errorMessage={formik.errors.name}
					type="name"
				/>
				<Input
					id="fiscal_id"
					name="fiscal_id"
					placeholder="Digite somente números"
					label="CPF ou CNPJ"
					value={formik.values.fiscal_id || ""}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.fiscal_id && formik.errors.fiscal_id}
					errorMessage={formik.errors.fiscal_id}
					type="text"
				/>
				<Input
					id="email"
					name="email"
					placeholder="Digite seu e-mail aqui"
					label="Email"
					disabled={!!logged_user}
					value={formik.values.email || ""}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.email && formik.errors.email}
					errorMessage={formik.errors.email}
					type="email"
				/>
				<Input
					id="cellphone"
					name="cellphone"
					placeholder="XX XXXXX-XXXX"
					label="Telefone Celular"
					disabled={!!logged_user}
					value={formik.values.cellphone || ""}
					onChange={formik.handleChange}
					onBlur={formik.handleBlur}
					error={formik.touched.cellphone && formik.errors.cellphone}
					errorMessage={formik.errors.cellphone}
					type="cellphone"
				/>
				{!logged_user && (
					<>
						<Input
							id="password"
							name="password"
							placeholder="Digite sua senha aqui"
							label="SENHA"
							type="password"
							value={formik.values.password}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.password && formik.errors.password}
							errorMessage={formik.errors.password}
						/>
						<div>
							<PasswordInstruction
								isValid={formik.values.password.length >= 8}
								label="A senha deve ter pelo menos 8 caracteres"
							/>
							<PasswordInstruction
								isValid={/[A-Z]/.test(formik.values.password)}
								label="1 letra maiúscula"
							/>
							<PasswordInstruction
								isValid={/[0-9]/.test(formik.values.password)}
								label="1 número"
							/>
							<PasswordInstruction
								isValid={/.*[!@#$%^&*+_=].*/.test(formik.values.password)}
								label="1 caractere especial (! @ # $ % &)"
							/>
						</div>
						<Input
							id="passwordConfirmation"
							name="passwordConfirmation"
							placeholder="Digite sua senha aqui"
							label="REPITA A SENHA"
							type="password"
							value={formik.values.passwordConfirmation}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={
								formik.touched.passwordConfirmation &&
								formik.errors.passwordConfirmation
							}
							errorMessage={formik.errors.passwordConfirmation}
						/>
					</>
				)}
				<Checkbox
					name="notifications"
					id="notifications"
					value="1"
					label={
						<span>
							Quero receber avisos de novos pedidos de viagem do VANbora
						</span>
					}
					onChange={onChangeNotifications}
					onBlur={formik.handleBlur}
					checked={formik.values.notifications}
					error={formik.touched.notifications && formik.errors.notifications}
				/>
				{!logged_user && (
					<Checkbox
						name="accept"
						id="accept"
						value="1"
						label={
							<span>
								Li e concordo com os{" "}
								<button
									type="button"
									role="link"
									onClick={() => openUseTerms()}
									style={{
										color: "#0066FF",
										background: "none",
										border: "none",
										padding: 0
									}}
								>
									Termos de Uso
								</button>{" "}
								e a{" "}
								<button
									type="button"
									role="link"
									onClick={() => openPrivacyPolicy()}
									style={{
										color: "#0066FF",
										background: "none",
										border: "none",
										padding: 0
									}}
								>
									Política de Privacidade
								</button>
								.
							</span>
						}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						checked={formik.values.accept}
						error={formik.touched.accept && formik.errors.accept}
					/>
				)}
			</div>
			<div className={styles.buttonWrapper}>
				<Button type="submit">{partnerId ? "Salvar" : "Próxima"}</Button>
			</div>
		</form>
	);
}
